/* eslint-disable no-unused-vars */
import React from 'react';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { Box, Grid, Chip, Accordion } from '@mui/material';
import TabList from '@mui/lab/TabList';
import { styled as muiStyled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import Typography from '@atoms/Typography';
import AudioPlayer from '@molecules/AudioPlayer';

export const Container = muiStyled(Grid)({});

export const SectionBox = muiStyled(Box)(({ theme }) => ({
  '&&': {
    padding: '30px 0',
    '& h6': {
      color: theme.secondaryColor[600],
      fontWeight: 700,
    },
  },
}));

export const StyledTitle = muiStyled('div')(({ theme }) => ({
  '& h6': {
    paddingLeft: '25px',
  },
  '& h5': {
    borderLeft: `3px solid ${theme.primary.main}`,
    paddingLeft: '10px',
  },
}));

export const StyledTabList = muiStyled(TabList)({
  '& .MuiTabs-indicator': {
    height: '1px',
  },
});

export const Header = muiStyled(Grid)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
});

export const ExternalLink = muiStyled('a')(({ theme }) => ({
  color: theme.primary.main,
  textDecoration: 'none',
}));

export const ApplicationName = muiStyled('div')({
  display: 'flex',
  flexDirection: 'column',
  marginLeft: '1rem',
  flexGrow: 1,
});

const ApplicationMetaBase = React.forwardRef(({ variant, ...props }, ref) => (
  <Typography ref={ref} variant={variant} {...props} />
));

ApplicationMetaBase.displayName = 'ApplicationMetaBase';
ApplicationMetaBase.propTypes = {
  variant: PropTypes.string,
};
ApplicationMetaBase.defaultProps = {
  variant: 'body1',
};

export const ApplicationMeta = muiStyled(ApplicationMetaBase)(({ theme }) => ({
  '&&': {
    lineHeight: 1,
    alignItems: 'center',
    display: 'flex',
    margin: '1rem 0',
    color: theme.secondaryColor[800],
  },
  '& svg': {
    fontSize: '1.2rem',
    marginRight: '0.2rem',
  },
}));

export const StyledBox = muiStyled(Box)(({ theme }) => ({
  '&&': {
    color: theme.secondaryColor[700],
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      color: theme.secondaryColor[700],
      marginRight: '20px',
    },
  },
}));

export const ActionContainer = muiStyled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'flex-end',
  '& button': {
    borderRadius: 0,
    marginBottom: '0.2rem',
  },
});

export const DetailsContainer = muiStyled('pre')({
  marginTop: 0,
  whiteSpace: 'pre-wrap',
  wordWrap: 'normal',
  wordBreak: 'keep-all',
  fontFamily: "'Questrial', 'Poppins', 'BlinkMacSystemFont'",
  lineHeight: '2rem',
  fontSize: '14px',
});

export const SocialContainer = muiStyled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  margin: '1.5rem 0',
  '& a': {
    marginRight: '1.5rem',
    marginLeft: '1.5rem',
  },
});

export const LinkContainer = muiStyled('div')(({ theme }) => ({
  display: 'flex',
  padding: '5px 0',
  marginLeft: '1rem',
  '&& a': {
    display: 'flex',
    color: theme.primary.main,
    alignItems: 'center',
    textDecoration: 'underline',
  },
  '&& a svg': {
    fontSize: '1.3rem',
    color: theme.secondaryColor[600],
  },
}));

export const StyledChip = muiStyled(Chip)(({ theme }) => ({
  marginRight: '10px',
  backgroundColor: theme.secondaryColor[150],
}));

export const StyledAccordion = muiStyled(Accordion)({
  width: '100%',
  boxShadow: 'none',
  border: '1px solid rgba(25, 25, 25, 0.32)',
  borderRadius: '6px',
  marginBottom: '10px',
  '&:before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root.Mui-expanded': {
    borderBottom: '1px solid rgba(25, 25, 25, 0.32)',
    minHeight: '48px',
  },
  '& .gGkPVc .MuiAccordionDetails-root': {
    padding: '15px 0',
  },
  '& .MuiAccordionSummary-content.Mui-expanded': {
    margin: '12px 0',
  },
  '& .MuiAccordionDetails-root': {
    padding: '15px 0',
    display: 'block',
  },
});

export const AccordionBoxes = muiStyled(Box)({
  display: 'flex',
  padding: '10px 55px',
  '& .boxTitle': {
    marginRight: '10px',
    textTransform: 'uppercase',
    color: '#757575',
    fontWeight: 500,
  },
});

export const GroupMembersField = muiStyled('div')({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  '& svg': {
    marginRight: '15px',
  },
});

export const Section = muiStyled(Grid)(({ theme }) => ({
  '& .elementName': {
    color: theme.secondaryColor[600],
  },
  '& .elementValue': {
    color: theme.secondaryColor[800],
    marginLeft: '1rem',
  },
  '& .elementSubValue': {
    color: theme.secondaryColor[200],
    marginLeft: '1rem',
  },
}));

export const Heading = muiStyled('div')({
  '&& h3, && h4': {
    marginTop: '2rem',
    marginBottom: '1rem',
  },
});

export const Attachment = muiStyled('a')({
  textDecoration: 'none',
  overflow: 'hidden',
  '& .MuiChip-root': {
    width: '100%',
  },
});

export const Facebook = muiStyled(FacebookIcon)({
  color: '#3b5999',
});

export const Twitter = muiStyled(TwitterIcon)({
  color: '#55acee',
});

export const Instagram = muiStyled(InstagramIcon)({
  color: '#e4405f',
});

export const YouTube = muiStyled(YouTubeIcon)({
  color: '#cd201f',
});

export const Linkedin = muiStyled(LinkedInIcon)({
  color: '#0A66C2',
});

export const StyledAudioPlayer = muiStyled(AudioPlayer)(({ theme }) => ({
  borderRadius: '6px',
  boxShadow: 'none',
  border: `1px solid ${theme.secondaryColor[150]}`,
  '& .rhap_main': {
    flexDirection: 'row-reverse',
  },
  '& .rhap_stacked .rhap_controls-section': {
    marginTop: 0,
    width: 0,
  },
  '& .rhap_progress-indicator': {
    display: 'none',
  },
  '& .rhap_controls-section .rhap_main-controls svg path': {
    fontSize: '30px !important',
    fill: theme.primary.main,
  },
  '& .rhap_time, & .rhap_total-time': {
    color: theme.secondary.dark,
  },
  '& .rhap_volume-controls': {
    display: 'none',
  },
  '& .rhap_additional-controls': {
    display: 'none',
  },
  '& .rhap_progress-filled': {
    backgroundColor: theme.primary.main,
  },
  '& .rhap_download-progress': {
    backgroundColor: theme.red[200],
  },
}));

export const SubmissionSectionStack = muiStyled(Box)({
  display: 'grid',
  gap: '1rem',
});

export const SubmissionSectionSurface = muiStyled(Box)(({ theme }) => ({
  border: `1px solid ${theme.secondaryColor[200]}`,
  borderLeft: `3px solid ${theme.primaryColor[100]}`,
  borderRadius: '14px',
  background: 'linear-gradient(180deg, rgba(252, 253, 255, 1) 0%, rgba(255, 255, 255, 1) 100%)',
  boxShadow: '0 8px 22px rgba(9, 23, 68, 0.04)',
  padding: '0 1.25rem',
  '& > div': {
    paddingTop: '1.15rem !important',
    paddingBottom: '1.15rem !important',
  },
  '& .MuiButton-root': {
    marginLeft: '0 !important',
    justifyContent: 'flex-start',
    paddingLeft: 0,
    paddingRight: 0,
  },
  '& .MuiChip-root': {
    marginLeft: '0 !important',
    marginRight: '0.65rem',
    marginTop: '0.65rem',
    backgroundColor: theme.primaryColor[25],
    border: `1px solid ${theme.primaryColor[100]}`,
    color: theme.primaryColor[700],
  },
  '& .elementValue, & .elementSubValue': {
    marginLeft: '0 !important',
    lineHeight: 1.75,
  },
  '& .elementSubValue': {
    color: theme.secondaryColor[600],
  },
  '& h5, & h6': {
    color: theme.secondaryColor[900],
    letterSpacing: '-0.01em',
  },
  [theme.breakpoints.down('md')]: {
    padding: '0 0.95rem',
  },
}));